/**
 * API Client
 *
 * Simple fetch wrapper for backend communication.
 * Uses native fetch - no external dependencies.
 */

import type { QuotaScope } from "./types";

const API_BASE = "/api";

/**
 * Custom error class for API errors with status code and optional data.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
    /** Seconds from the `Retry-After` header, when the server sent an integer one (429 quota / rate limit). */
    public retryAfterSecs?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Set by the backend on a 2xx the quota fallback tier served (FLOW-790). Its
 * value is the reason: `quota_exhausted` at the org's cap, and since FLOW-930
 * `project_budget_exhausted` when one project's monthly budget was the ceiling.
 */
export const AI_FALLBACK_HEADER = "x-ai-fallback";

/**
 * An AI output plus whether the fallback tier produced it (FLOW-794) and, when
 * the backend named one, which ceiling bound it (FLOW-930). An unrecognised
 * header value leaves `degradedScope` absent, so a future reason reads as
 * "degraded, scope unknown" rather than as a project ceiling.
 */
export type AiServed<T> = T & {
  degraded: boolean;
  degradedScope?: QuotaScope;
};

/**
 * HTTP-date forms are not parsed: the backend only ever sends delta-seconds.
 *
 * Bounded to a month (`31 * 24 * 3600`) — comfortably past the daily and
 * monthly caps this header is ever set from. A value above that (hostile
 * proxy, or a future backend bug) is treated as absent rather than let
 * through to overflow into an Invalid Date downstream.
 */
const RETRY_AFTER_CEILING_SECS = 31 * 24 * 3600;

export function parseRetryAfter(value: string | null): number | undefined {
  if (value === null || !/^\d+$/.test(value.trim())) return undefined;
  const parsed = Number.parseInt(value, 10);
  return parsed > RETRY_AFTER_CEILING_SECS ? undefined : parsed;
}

/**
 * Core request function with automatic JSON handling and error management.
 * Returns the parsed JSON response alongside the response itself, for callers
 * that need to read headers.
 */
async function send<T>(
  path: string,
  options?: RequestInit,
): Promise<{ data: T; response: Response }> {
  // Judged against the path the request was issued from, not where the
  // response lands: a 401 for a /board request that arrives after a
  // client-side navigation to / must still send the user to sign in.
  const issuedFrom = window.location.pathname;
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    // Redirect to login on 401 (unless already on login, pending, the
    // public invite page, the public legal pages, or the landing page — an
    // unauthenticated visitor there must not be bounced away from the invite
    // they're trying to accept, from the terms/privacy page they navigated to
    // directly, or from the landing page, which is the public entry point and
    // probes /auth/me purely to forward a signed-in visitor to the app).
    // /, /terms and /privacy are exact-match routes in App.tsx (unlike
    // /invite/:token), so they're checked with strict equality rather than
    // startsWith — every in-app path begins with "/", and a future route like
    // /privacy/export must not silently inherit this allowlisting.
    if (
      response.status === 401 &&
      !issuedFrom.startsWith("/login") &&
      !issuedFrom.startsWith("/pending") &&
      !issuedFrom.startsWith("/invite") &&
      issuedFrom !== "/" &&
      issuedFrom !== "/terms" &&
      issuedFrom !== "/privacy"
    ) {
      window.location.href = "/login";
      throw new ApiError(401, "Unauthorized");
    }

    const data = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      (data as { error?: string })?.error ?? response.statusText,
      data,
      parseRetryAfter(response.headers.get("Retry-After")),
    );
  }

  const text = await response.text();
  if (!text) {
    return { data: null as T, response };
  }

  return { data: JSON.parse(text) as T, response };
}

/** Parsed JSON body of a successful request. */
async function request<T>(path: string, options?: RequestInit): Promise<T> {
  return (await send<T>(path, options)).data;
}

/**
 * Fold a response's fallback header into the body, for the one set of rules
 * every AI endpoint agrees on — shared by `postAi` and `getAiWithStatus` so
 * the two cannot drift.
 *
 * A boolean `degraded` in the body wins (persisted truth); otherwise the
 * header's presence decides. The header's *value* names the ceiling, and that
 * scope rides along only on an output that is actually degraded — a body that
 * says the standard model served it must not carry a scope even when the
 * header is present on the same response (a cached standard-model output read
 * while a ceiling holds). An unrecognised value leaves `degradedScope` absent,
 * so a future reason reads as "degraded, scope unknown" rather than as a
 * project ceiling.
 */
function aiServed<T>(data: T, response: Response): AiServed<T> {
  const own = (data as { degraded?: unknown } | null)?.degraded;
  const reason = response.headers.get(AI_FALLBACK_HEADER);
  const degraded = typeof own === "boolean" ? own : reason !== null;
  const scope: QuotaScope | undefined =
    reason === "quota_exhausted"
      ? "tenant"
      : reason === "project_budget_exhausted"
        ? "project"
        : undefined;
  return degraded && scope
    ? { ...data, degraded, degradedScope: scope }
    : { ...data, degraded };
}

/**
 * HTTP method helpers for common operations.
 */
export const api = {
  /** GET request - expects JSON response */
  get: <T>(path: string) => request<T>(path),

  /**
   * GET request that also surfaces the response status (e.g. 200 vs 202).
   * Header-blind on purpose: its callers (the breakdown poll) read endpoints
   * that never set the fallback header.
   */
  getWithStatus: async <T>(
    path: string,
  ): Promise<{ data: T; status: number }> => {
    const { data, response } = await send<T>(path);
    return { data, status: response.status };
  },

  /**
   * `getWithStatus` for an AI endpoint: same status passthrough, but the
   * fallback header is folded into the body under the rules in `aiServed`.
   * The FLOW-886 auto-prioritize poll needs this — admission happens on the
   * POST that returns 202, so the scope only ever reaches the client on the
   * status GET's 200 (FLOW-930).
   */
  getAiWithStatus: async <T>(
    path: string,
  ): Promise<{ data: AiServed<T>; status: number }> => {
    const { data, response } = await send<T>(path);
    return { data: aiServed(data, response), status: response.status };
  },

  /** POST request - sends JSON body, expects JSON response */
  post: <T>(path: string, body: unknown, extra?: RequestInit) =>
    request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
      ...extra,
    }),

  /** POST to an AI endpoint, reading the fallback header per `aiServed`. */
  postAi: async <T>(
    path: string,
    body: unknown,
    extra?: RequestInit,
  ): Promise<AiServed<T>> => {
    const { data, response } = await send<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
      ...extra,
    });
    return aiServed(data, response);
  },

  /** PATCH request - sends JSON body, expects JSON response */
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** PUT request - sends JSON body, expects JSON response */
  put: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** DELETE request - typically returns no content */
  delete: (path: string) =>
    request<void>(path, {
      method: "DELETE",
    }),

  /** DELETE request with expected response body */
  deleteWithResponse: <T>(path: string) =>
    request<T>(path, {
      method: "DELETE",
    }),
};
